import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { auditPath } from '../utils/auditRoutes';
import { PageFrame } from '../components/PageFrame';
import { SectionCard } from '../components/SectionCard';
import { RatingGroup } from '../components/RatingGroup';
import { TechStackSection } from '../components/TechStackSection';
import { NavigationControls } from '../components/NavigationControls';
import { SectionRecapCard } from '../components/SectionRecapCard';
import { MessageCircle, Headset, Lightbulb } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const questions = [
  'Customers have to wait too long for responses from our team.',
  'We struggle to track customer interactions across channels.',
  'Our team lacks real-time access to customer history or preferences.',
  'We can\'t personalize communication at scale.',
  'Customer feedback is difficult to gather and act on.'
];

export default function CustomerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditData, updateSectionRating, updateSectionNotes } = useAudit();
  const section = auditData.sections.customer;
  const handleNext = () => {
    navigate(auditPath('/marketing', location.pathname));
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  return (
    <PageFrame pageNumber={4} backgroundStyle="light">
      <div className="container mx-auto px-6 py-12 bg-gray-50/50">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="bg-primary text-white rounded-lg p-6 text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide w-full sm:w-auto mx-auto" data-testid="section-title">
              Section 2 – Customer Experience
            </h1>
          </div>

          <SectionCard
            title="Rate Each Statement"
            description="How true is each statement for your business?"
            icon={MessageCircle}
          >
            <div className="space-y-8">
              {questions.map((question, index) => (
                <div key={index} className="pb-6 border-b border-muted last:border-0">
                  <RatingGroup
                    value={section.ratings[index]}
                    onChange={(rating) => updateSectionRating('customer', index, rating)}
                    questionId={`customer-q${index + 1}`}
                    label={`${index + 1}. ${question}`}
                  />
                </div>
              ))}
            </div>
          </SectionCard>

          <div className="flex items-center justify-between bg-muted rounded-lg p-4 mt-4">
            <span className="text-secondary font-semibold">Section Total:</span>
            <span className="text-3xl font-bold text-primary" data-testid="customer-total">
              {section.total} <span className="text-base text-muted-foreground">/ 25</span>
            </span>
          </div>

          <SectionRecapCard
            sectionKey="customer"
            section={section}
            tools={auditData.tech_stack?.customer?.tools || []}
            integrationRating={auditData.tech_stack?.customer?.integration_rating || 0}
          />

          <div className="bg-primary text-white rounded-lg p-6 mt-6">
            <div className="flex items-start gap-4">
              <Lightbulb className="h-8 w-8 flex-shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="font-bold text-lg mb-2">Insight</h3>
                <p>
                  High scores indicate opportunities for AI chatbots, sentiment analysis, automated feedback collection, and personalized customer journey mapping.
                </p>
              </div>
            </div>
          </div>

          <SectionCard title="Reflection" icon={Headset} className="mt-6">
            <div className="space-y-2">
              <Label htmlFor="customer-notes">What customer experience gap concerns you most? (Optional)</Label>
              <Textarea
                id="customer-notes"
                value={section.notes}
                onChange={(e) => updateSectionNotes('customer', e.target.value)}
                placeholder="Share your thoughts here..."
                className="min-h-32 resize-y"
                data-testid="customer-notes-textarea"
              />
              <p className="text-xs text-muted-foreground">Optional. More detail helps us personalize the report.</p>
            </div>
          </SectionCard>

          <div className="mt-6">
            <TechStackSection category="customer" />
          </div>

          <div className="pt-4">
            <NavigationControls currentPage={4} customNext={handleNext} />
          </div>
        </div>
      </div>
    </PageFrame>
  );
}